import { useEffect, useRef, useCallback, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Hook for managing WebSocket connection and real-time notifications
 */
export const useNotifications = () => {
  const { token, user, isAuthenticated } = useAuth();
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const [isConnected, setIsConnected] = useState(false);
  const [notifications, setNotifications] = useState([]);

  // Get WebSocket URL
  const getWsUrl = useCallback(() => {
    if (!API_URL || !token) return null;
    const wsProtocol = API_URL.startsWith('https') ? 'wss' : 'ws';
    const baseUrl = API_URL.replace(/^https?/, wsProtocol);
    return `${baseUrl}/ws/notifications?token=${token}`;
  }, [token]);

  // Show notification toast based on type
  const showNotificationToast = useCallback((notification) => {
    const { type, title, message, priority } = notification;
    
    // Determine toast type
    const isHighPriority = priority === 'high';
    
    switch (type) {
      case 'cleaner_clocked_in':
        toast.success(title, {
          description: message,
          duration: isHighPriority ? 10000 : 5000,
          icon: '🏠',
        });
        break;
      
      case 'cleaner_clocked_out':
        toast.success(title, {
          description: message,
          duration: 8000,
          icon: '✨',
        });
        break;
      
      case 'payment_successful':
        toast.success(title, {
          description: message,
          icon: '💳',
        });
        break;
      
      case 'payment_failed':
        toast.error(title, {
          description: message,
          duration: 15000,
          icon: '⚠️',
        });
        break;
      
      case 'new_booking':
        toast.info(title, {
          description: message,
          icon: '📅',
        });
        break;
      
      case 'gps_flag':
        toast.warning(title, {
          description: message,
          duration: 10000,
          icon: '📍',
        });
        break;
      
      case 'job_assigned':
        toast.success(title, {
          description: message,
          duration: 10000,
          icon: '🧹',
        });
        break;
      
      case 'appointment_confirmed':
        toast.success(title, {
          description: message,
          icon: '✓',
        });
        break;

      case 'new_lead':
        toast.info(title, {
          description: message,
          duration: 12000,
          icon: '🔔',
        });
        break;
      
      case 'connected':
        // Don't show connection toast
        break;
      
      default:
        toast(title, {
          description: message,
        });
    }
  }, []);

  // Connect to WebSocket
  const connect = useCallback(() => {
    const wsUrl = getWsUrl();
    if (!wsUrl || wsRef.current?.readyState === WebSocket.OPEN) {
      return;
    }

    try {
      const ws = new WebSocket(wsUrl);
      
      ws.onopen = () => {
        setIsConnected(true);
      };
      
      ws.onmessage = (event) => {
        try {
          const notification = JSON.parse(event.data);
          
          // Add unique id and read status
          const enriched = {
            ...notification,
            id: notification.id || `notif_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
            read: false,
          };
          
          // Store notification
          setNotifications(prev => [enriched, ...prev].slice(0, 50));
          
          // Show toast
          showNotificationToast(notification);
        } catch (_) {
          // Failed to parse notification
        }
      };
      
      ws.onclose = (event) => {
        setIsConnected(false);
        wsRef.current = null;
        
        // Reconnect after delay (if not intentional close)
        if (event.code !== 1000 && isAuthenticated) {
          reconnectTimeoutRef.current = setTimeout(() => {
            connect();
          }, 5000);
        }
      };
      
      ws.onerror = () => {
        // WebSocket error handled by onclose reconnect
      };
      
      wsRef.current = ws;
    } catch (_) {
      // Failed to connect WebSocket
    }
  }, [getWsUrl, isAuthenticated, showNotificationToast]);

  // Disconnect from WebSocket
  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    if (wsRef.current) {
      wsRef.current.close(1000, 'User logout');
      wsRef.current = null;
    }
    setIsConnected(false);
  }, []);

  // Connect on auth change
  useEffect(() => {
    if (isAuthenticated && token) {
      connect();
    } else {
      disconnect();
    }
    
    return () => {
      disconnect();
    };
  }, [isAuthenticated, token, connect, disconnect]);

  // Ping to keep connection alive
  useEffect(() => {
    const pingInterval = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send('ping');
      }
    }, 30000);
    
    return () => clearInterval(pingInterval);
  }, []);

  // Clear notifications
  const clearNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  // Mark notification as read
  const markAsRead = useCallback((notificationId) => {
    setNotifications(prev => 
      prev.map(n => 
        n.id === notificationId ? { ...n, read: true } : n
      )
    );
  }, []);

  return {
    isConnected,
    notifications,
    unreadCount: notifications.filter(n => !n.read).length,
    clearNotifications,
    markAsRead,
  };
};

export default useNotifications;
